import IconButton from '@/global/components/Button/IconButton'
import ImprimirButton from '@/global/components/DataGrid/components/CustomGridToolBar/ImprimirButton'
import { GridButton } from '@/global/components/DataGrid/components/CustomGridToolBar/MenuComponents'
import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import useDefaultFilterModel from '@/global/components/DataGrid/hooks/useDefaultFilterModel'
import useDefaultSortModel from '@/global/components/DataGrid/hooks/useDefaultSortModel'
import FilterModelPro from '@/global/components/DataGrid/utils/filterModelPro'
import { useTableApiContext } from '@/global/components/DataGrid/utils/table-context'
import { GridColDefPro } from '@/global/components/DataGrid/utils/types'
import {
    filterLocaisCentro,
    groupBy,
    hoursToMinutes,
    minutesToHours,
} from '@acme/common'
import { Box } from '@mui/material'
import IconeAjuda from 'global/components/IconeAjuda'
import SelectCompetencia, {
    useCompetencia,
} from 'global/components/Selects/SelectCompetencia'
import ContentGuide from 'global/guides/ContentGuide'
import { hasPermission } from 'hooks/useBackendMenu'
import { ListIcon, SettingsIcon } from 'lucide-react'
import React, { useState } from 'react'
import { Link, Outlet, useOutletContext } from 'react-router-dom'
import {
    ValeAlimentacao,
    valeAlimentacao,
    ValoresValeAlimentacao,
    valoresValeAlimentacao,
} from 'service/api'
import { useStore } from 'store'

const columns: GridColDefPro<ValoresValeAlimentacao>[] = [
    {
        field: 'Funcionario',
        headerName: 'Funcionário',
        flex: 1,
        valueGetter: ({ value }) => value.nome,
    },
    {
        field: 'valorTotal',
        headerName: 'Valor total',
        width: 180,
        headerAlign: 'right',
        align: 'right',
        type: 'number',
        valueGetter: ({ value }) => Number(value).toFixed(2),
        valueFormatter: ({ value }) =>
            `R$ ${value.toString().replace('.', ',')}`,
    },
    {
        field: 'horasTrabalhadas',
        headerName: 'Horas trabalhadas',
        width: 150,
        headerAlign: 'right',
        align: 'right',
    },
    {
        ...optionsColumn,
        field: '__detalhes',
        renderCell: ({ row: { funcionarioId } }) => (
            <IconButton href={`/relatorios/vale-alimentacao/${funcionarioId}`}>
                <ListIcon />
            </IconButton>
        ),
    },
]

export default function RelatorioValeAlimentacao() {
    const [filterModel, setFilterModel] = useDefaultFilterModel()
    const [competencia, setCompetencia] = useCompetencia()
    const [isImprimindo, setIsImprimindo] = useState<boolean>(false)
    const empresaId = useStore(state => state.companyId)
    const centroCustoId = useStore(state => state.costCenterId)
    const locaisIds = useStore(state => state.locaisIds)
    const { data: configuracao, isFetched } =
        valeAlimentacao.useFetch<ValeAlimentacao>({
            queryParams: { empresaId },
            enabled: empresaId > 0,
        })
    const {
        data = [],
        isLoading,
        isFetched: isDataFetched,
    } = valoresValeAlimentacao.useFetch({
        queryParams: {
            empresaId,
            competencia,
        },
        enabled: empresaId > 0 && !!configuracao,
    })

    const rows = React.useMemo(
        () =>
            Object.values(groupBy(data, 'funcionarioId'))
                .map(item => ({
                    id: item[0].funcionarioId,
                    funcionarioId: item[0].funcionarioId,
                    Funcionario: (item[0] as any).Funcionario,
                    competencia: item[0].competencia,
                    horasTrabalhadas: minutesToHours(
                        item.reduce(
                            (acc: number, curr: any) =>
                                acc + hoursToMinutes(curr.horasTrabalhadas),
                            0,
                        ),
                    ),
                    diasTrabalhados: item.reduce(
                        (acc: any, curr: any) => {
                            if (curr.porcentagem > 0)
                                return { ...acc, [curr.data]: curr.porcentagem }
                            else return acc
                        },
                        {} as Record<string, number>,
                    ),
                    valorTotal: item.reduce(
                        (acc: number, curr: any) => acc + curr.valorTotal,
                        0,
                    ),
                }))
                .filter(filterLocaisCentro(locaisIds, [centroCustoId]))
                .sort((a, b) =>
                    a.Funcionario.nome.localeCompare(b.Funcionario.nome),
                ),
        [data, centroCustoId, locaisIds],
    )

    const [sortModel, setSortModel, sort] = useDefaultSortModel(columns)

    const handleClick = async () => {
        setIsImprimindo(true)
        try {
            await valoresValeAlimentacao.pdf({
                data: sort(
                    rows.filter(
                        new FilterModelPro(filterModel, columns).filter,
                    ),
                ).map(({ funcionarioId }) => funcionarioId),
                queryParam: {
                    empresaId,
                    competencia,
                },
            })
        } finally {
            setIsImprimindo(false)
        }
    }

    return (
        <ContentGuide>
            <ContentGuide.Card>
                <ContentGuide.Header label={['Relatórios', 'Vale alimentação']}>
                    <ContentGuide.Filtros
                        SelectEmpresas
                        SelectCentroCusto
                        SelectLocais
                    >
                        <SelectCompetencia
                            onChange={setCompetencia}
                            value={competencia}
                        />
                    </ContentGuide.Filtros>
                </ContentGuide.Header>
                <ContentGuide.DataGrid
                    error={
                        !data.length && isDataFetched && !configuracao
                            ? new Error(
                                  'Empresa não possui configuração de vale alimentação,' +
                                      ' clique em configurar para definir os valores.',
                              )
                            : undefined
                    }
                    loading={isLoading}
                    filterModel={filterModel}
                    onFilterModelChange={setFilterModel}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    rows={rows}
                    ToolbarProps={{
                        exportButtonProps: {
                            fileName: 'exportacao-vale-alimentacao',
                        },
                    }}
                    columns={columns}
                    components={{
                        Toolbar: () => (
                            <>
                                {hasPermission([
                                    'update',
                                    'ValeAlimentação',
                                ]) && (
                                    <GridButton
                                        href="configuracao"
                                        label="Configurar"
                                        startIcon={<SettingsIcon />}
                                    />
                                )}
                                <div style={{ flex: 1 }} />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    <ImprimirButton
                                        handleClick={handleClick}
                                        isImprimindo={isImprimindo}
                                        disabled={isFetched && !configuracao}
                                    />
                                    {isFetched && !configuracao && (
                                        <IconeAjuda>
                                            Configurações não definidas, clique
                                            <Link
                                                style={{
                                                    color: 'royalblue',
                                                }}
                                                to="configuracao"
                                            >
                                                {` aqui `}
                                            </Link>
                                            para configurar os valores do
                                            relatório.
                                        </IconeAjuda>
                                    )}
                                </Box>
                            </>
                        ),
                        Footer,
                    }}
                />
            </ContentGuide.Card>
            <Outlet
                context={{
                    data,
                    Config: { data: configuracao, isFetched },
                }}
            />
        </ContentGuide>
    )
}

function Footer() {
    const { table } = useTableApiContext()
    const tableRows = table?.getCoreRowModel().flatRows
    if (!tableRows || tableRows.length === 0) return
    return (
        <span>
            <strong>Total a ser pago:</strong>
            &nbsp;&nbsp;&nbsp;
            {new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
            }).format(
                tableRows.reduce(
                    (acc, row) => acc + row.original.valorTotal,
                    0,
                ),
            )}
        </span>
    )
}

export function useValeAlimentacaoOutletContext() {
    return useOutletContext<{
        data?: {
            horasTrabalhadas: string
            valorTotal: number
            funcionarioId: number
            competencia: string
            porcentagemReceber: number
            data: Date
        }[]
        Config: {
            data?: ValeAlimentacao
            isFetched: boolean
        }
    }>()
}
